/**
 * OpportunityActivityTab.jsx - the opportunity editor's Activity tab (AL-01,
 * AL-09): the entries TAGGED to this opportunity plus its own events
 * (created, status, sales stage, site link, its tasks).
 *
 * Logging from here puts the entry on the opportunity's linked site
 * (projects.account_id - an entry always lives on an account or site) and
 * pre-tags this opportunity. With no linked site there is nowhere to put
 * one, so the button gives way to a note; the events still show.
 *
 * The form takes no next step here: its task would land on the site as a
 * lead task, never on this opportunity. The opportunity's own next step is
 * added on its Tasks tab.
 *
 * Opportunity chips on the timeline never navigate this tab away: this one's
 * is plain text, another opens in a new browser tab.
 *
 * Its own file on purpose: EditOpportunityModal also runs on the GLOBAL KSA
 * tab, whose context value must cover every useApp() key the modal reads
 * (server/test_ksa.js). The modal never shows this tab there, and this
 * renders nothing in that scope anyway.
 */
import { useMemo, useState } from 'react';
import { Icon } from '../ui/Icon';
import { Toast } from '../ui/Toast';
import { useApp } from '../../context/AppContext';
import { useDataScope } from '../../context/DataScope';
import { pickField } from '../../data/crmStages';
import { accountLabel, indexById } from '../crm/nextStep';
import { BTN_PRIMARY } from '../crm/crmStyles';
import Timeline from '../crm/Timeline';
import ActivityForm from '../crm/ActivityForm';

export default function OpportunityActivityTab({ opp, editable, deletable }) {
  const scope = useDataScope();
  const { allAccounts, allProjects, fetchAll } = useApp();
  const [showForm, setShowForm] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [toast, setToast] = useState('');

  const oppId = opp && opp.id != null ? String(opp.id) : '';
  // the stored link, not an unsaved pick on the Summary tab
  const live = useMemo(() => (allProjects || []).find((p) => p && String(p.id) === oppId) || opp, [allProjects, oppId, opp]);
  const siteId = pickField(live, 'accountId', 'account_id');
  const accountsById = useMemo(() => indexById(allAccounts), [allAccounts]);
  const site = siteId ? accountsById.get(String(siteId)) : null;

  if (scope || !oppId) return null;

  const refresh = () => {
    setRefreshKey((k) => k + 1);
    if (typeof fetchAll === 'function') fetchAll();
  };

  return (
    <div>
      {toast && <Toast message={toast} onDone={() => setToast('')} />}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#0D0D0D' }}>Activity</div>
          <div style={{ fontSize: 10.5, color: '#888', marginTop: 2 }}>
            Entries tagged to this opportunity, plus its own changes and tasks.
          </div>
        </div>
        {editable && site && !showForm && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            style={{ ...BTN_PRIMARY, padding: '7px 14px', display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}
          >
            <Icon name="plus" size={11} color="#fff" /> Log Activity
          </button>
        )}
      </div>

      {!site && (
        <div
          style={{
            fontSize: 11,
            color: '#8A6D00',
            background: '#FFF8E1',
            border: '1px solid #F2E2A0',
            borderRadius: 8,
            padding: '8px 12px',
            marginBottom: 14,
          }}
        >
          {siteId
            ? 'The site this opportunity is linked to is not on file. Link this opportunity to a site to log activity here.'
            : 'Link this opportunity to a site to log activity here.'}
          {editable && <span style={{ color: '#888' }}> Pick the Site / account on the Summary tab and save.</span>}
        </div>
      )}

      {showForm && site && (
        <div>
          <div style={{ fontSize: 10.5, color: '#555', fontWeight: 700, marginBottom: 6 }}>
            Logging on {accountLabel(site, accountsById)}, tagged to this opportunity. Add its next step on the Tasks tab.
          </div>
          <ActivityForm
            record={site}
            defaults={{ opportunity_ids: [oppId] }}
            hideNextStep
            onCancel={() => setShowForm(false)}
            onSaved={() => {
              setShowForm(false);
              setToast('Activity logged');
              refresh();
            }}
          />
        </div>
      )}

      <Timeline
        kind="opportunity"
        id={oppId}
        currentOpportunityId={oppId}
        refreshKey={refreshKey}
        editable={editable}
        deletable={deletable}
        onChanged={() => {
          if (typeof fetchAll === 'function') fetchAll();
        }}
      />
    </div>
  );
}
